import {
  buildApprovedPlanContext,
  normalizePlatformKey,
  platformLabelFromKey,
} from "./creativeStudioIntelligence";

const PLATFORM_PROFILES = {
  facebook: {
    performanceRole: "a social performance layer for audience development, retargeting, and conversion-ready engagement",
    audienceRole: "high-intent social audiences, retargeting pools, and conversion-ready communities around the campaign",
    platformRole: "translating upper-funnel attention into social proof, repeat exposure, and lower-funnel action",
    regionalRole: "market-level social demand signals and remarketing density once delivery data lands",
  },
  instagram: {
    performanceRole: "a visual social layer for reach, creative testing, and engagement-driven consideration",
    audienceRole: "visually responsive audiences who can move from awareness into saved, shared, or retargetable engagement",
    platformRole: "extending the campaign’s visual storytelling and keeping creative resonance strong across the broader mix",
    regionalRole: "placement and market signals tied to where visual social engagement concentrates",
  },
  tiktok: {
    performanceRole: "a short-form discovery layer designed for cultural reach, fast creative learning, and incremental social attention",
    audienceRole: "mobile-first audiences who respond to fast storytelling, creator-native pacing, and cultural momentum",
    platformRole: "injecting fresh attention into the campaign and revealing which creative hooks resonate earliest",
    regionalRole: "emerging pockets of short-form engagement once the platform resolves geo-level delivery detail",
  },
  youtube: {
    performanceRole: "a video storytelling layer built for scalable awareness, trailer completion, and upper-funnel attention",
    audienceRole: "viewers who need richer narrative context before moving deeper into the campaign journey",
    platformRole: "carrying the long-form narrative burden of the mix and qualifying engaged video viewers for downstream retargeting",
    regionalRole: "video-view concentration and completion pockets across the highest-response markets",
  },
  x: {
    performanceRole: "a real-time conversation layer for message amplification, launch moments, and rapid social reach",
    audienceRole: "news-aware and conversation-led audiences who react quickly to cultural or campaign moments",
    platformRole: "keeping the campaign present inside live conversation and reinforcing broader awareness pushes",
    regionalRole: "conversation density and response pockets around active markets and live moments",
  },
  display: {
    performanceRole: "a programmatic reminder layer for retargeting, frequency support, and site-revisit efficiency",
    audienceRole: "browsing and retargetable audiences who benefit from repeated visual reminders after initial exposure",
    platformRole: "reinforcing the wider mix with efficient reach, reminder messaging, and lower-cost repetition",
    regionalRole: "inventory and market concentration that show where reminder messaging is landing most efficiently",
  },
  video: {
    performanceRole: "a programmatic video layer for view-through storytelling, sequential messaging, and upper-funnel attention",
    audienceRole: "streaming audiences who respond to motion, narrative sequencing, and high-attention video environments",
    platformRole: "bridging premium awareness channels with measurable digital video completion and retargetable viewership",
    regionalRole: "market-level completion and video-delivery concentration as geo signals populate",
  },
  ctv: {
    performanceRole: "a premium streaming layer for household reach, lean-back attention, and high-quality awareness",
    audienceRole: "living-room and premium-streaming audiences who need higher-attention storytelling environments",
    platformRole: "delivering premium reach that anchors the campaign’s upper-funnel attention before other channels reinforce it",
    regionalRole: "household delivery clusters and premium-market concentration as CTV geo data resolves",
  },
  audio: {
    performanceRole: "an audio reinforcement layer for repeated message exposure, contextual reach, and daypart support",
    audienceRole: "commuting, multitasking, and background-listening audiences who benefit from repeated sonic cues",
    platformRole: "keeping the campaign present between higher-attention visual exposures and extending message frequency efficiently",
    regionalRole: "listening concentration by market and context once audio geo and placement data matures",
  },
  mobile: {
    performanceRole: "a mobile reinforcement layer for on-the-go reach, lightweight retargeting, and frequent touchpoints",
    audienceRole: "device-first audiences who are reachable throughout the day in short, repeatable moments",
    platformRole: "supporting the wider mix with portable frequency, rapid follow-up exposure, and mobile-first response opportunities",
    regionalRole: "location and delivery pockets where mobile touchpoints are compounding campaign frequency",
  },
  dooh: {
    performanceRole: "a geographic awareness layer for high-visibility repetition, launch amplification, and contextual presence",
    audienceRole: "commuters and place-based audiences who absorb repeated brand cues in shared physical environments",
    platformRole: "anchoring the mix with physical-world visibility that other digital channels can reinforce and convert",
    regionalRole: "market-level placement concentration and geographic reinforcement once delivery confirms the strongest zones",
  },
  google_search: {
    performanceRole: "an intent-capture layer for harvesting demand once the broader mix has created awareness",
    audienceRole: "high-intent searchers who are already signaling active interest in the campaign or brand",
    platformRole: "converting cross-platform demand into measurable lower-funnel action and efficient intent capture",
    regionalRole: "search-intent concentration in markets where the campaign is generating the strongest demand",
  },
  reddit: {
    performanceRole: "a community-driven reach layer for credibility, discussion, and targeted interest-based discovery",
    audienceRole: "interest-led communities that respond best to relevance, authenticity, and contextual participation",
    platformRole: "bringing the campaign into niche conversations that can deepen credibility beyond broad-reach channels",
    regionalRole: "community concentration where response and discussion volume begins to cluster",
  },
  amazon: {
    performanceRole: "a commerce-adjacent performance layer for high-intent shoppers and product consideration",
    audienceRole: "audiences already exhibiting shopping behavior or product-category intent",
    platformRole: "capturing measurable commerce intent after the wider mix has established demand",
    regionalRole: "shopping-response clusters once marketplace and regional performance data are available",
  },
  truth_social: {
    performanceRole: "a niche social reach layer for message extension into a defined audience environment",
    audienceRole: "distinct social communities that may not be reached efficiently through broader social channels alone",
    platformRole: "extending campaign presence into audience pockets that complement the broader social mix",
    regionalRole: "engagement pockets where niche audience concentration begins to show",
  },
  rumble: {
    performanceRole: "an alternative video reach layer for incremental streaming attention and audience diversification",
    audienceRole: "video consumers who sit outside the dominant streaming surfaces in the broader plan",
    platformRole: "adding incremental video reach and diversifying how the campaign shows up in streaming environments",
    regionalRole: "incremental market response as alternative video inventory begins to populate",
  },
  ingame: {
    performanceRole: "an immersive reach layer for attention inside interactive environments and gaming-heavy audiences",
    audienceRole: "gaming and interactive-entertainment audiences who respond to contextual, immersive placements",
    platformRole: "bringing the campaign into focused attention environments that complement broader awareness media",
    regionalRole: "inventory and response clusters where gaming audiences are concentrating delivery",
  },
  email: {
    performanceRole: "a direct-response support layer for known audiences, reminders, and retention messaging",
    audienceRole: "known or opted-in audiences who can move efficiently from reminder messaging to action",
    platformRole: "closing the loop on cross-platform demand with owned-audience follow-up and measurable response",
    regionalRole: "regional audience response where owned reach and follow-up engagement are strongest",
  },
};

const DEFAULT_PROFILE = {
  performanceRole: "a defined layer inside the approved media mix",
  audienceRole: "the audience cohort outlined in the approved campaign strategy",
  platformRole: "supporting the wider approved mix with a clear strategic job",
  regionalRole: "market-level reinforcement once delivery and geographic signals become available",
};

const KEYWORD_ROLE_HINTS = [
  { test: /retarget|remarket/i, value: "retargeting and reminder messaging" },
  { test: /convers|action|ticket|purchase|sale|lead/i, value: "conversion support and lower-funnel action" },
  { test: /awareness|reach|launch|announce|introduc/i, value: "upper-funnel awareness and broad reach" },
  { test: /engag|social proof|community/i, value: "engagement and community response" },
  { test: /video|story|trailer|view/i, value: "storytelling and view-through attention" },
];

const splitPlatformTokens = (value) =>
  String(value || "")
    .split(/\s*(?:,|\/|&|\+|\||\band\b)\s*/i)
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

function normalizeLooseKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function expandPlatformToken(value) {
  const direct = normalizePlatformKey(value);
  if (direct) return [direct];

  const normalized = normalizeLooseKey(value);
  if (!normalized) return [];

  if (["meta", "meta ads", "meta paid social"].includes(normalized)) {
    return ["facebook", "instagram"];
  }
  if (["paid social", "social", "social media"].includes(normalized)) {
    return ["facebook", "instagram", "tiktok", "x"];
  }
  if (normalized.includes("programmatic") && normalized.includes("display")) return ["display"];
  if (normalized.includes("programmatic") && normalized.includes("video")) return ["video"];
  if (normalized.includes("programmatic") && normalized.includes("audio")) return ["audio"];
  if (normalized.includes("programmatic") && normalized.includes("mobile")) return ["mobile"];
  if (normalized.includes("programmatic") && normalized.includes("dooh")) return ["dooh"];
  if (normalized.includes("programmatic") && normalized.includes("ctv")) return ["ctv"];
  if (normalized.includes("connected tv")) return ["ctv"];
  if (normalized.includes("google search") || normalized === "search") return ["google_search"];
  if (normalized.includes("out of home")) return ["dooh"];
  if (normalized.includes("in game")) return ["ingame"];

  return [];
}

function normalizePlanPlatformKeys(value) {
  const tokens = Array.isArray(value)
    ? value
    : typeof value === "string"
    ? splitPlatformTokens(value)
    : value && typeof value === "object"
    ? [
        ...(Array.isArray(value.platforms) ? value.platforms : []),
        ...(Array.isArray(value.targetPlatforms) ? value.targetPlatforms : []),
        ...(Array.isArray(value.target_platforms) ? value.target_platforms : []),
      ]
    : [];

  const resolved = new Set();
  tokens.forEach((token) => {
    expandPlatformToken(token).forEach((key) => resolved.add(key));
  });
  return Array.from(resolved);
}

function walkObjectStrings(input, depth = 0, visited = new Set(), collected = []) {
  if (!input || depth > 4) return collected;
  if (Array.isArray(input)) {
    input.forEach((item) => walkObjectStrings(item, depth + 1, visited, collected));
    return collected;
  }
  if (typeof input !== "object") return collected;
  if (visited.has(input)) return collected;
  visited.add(input);

  Object.entries(input).forEach(([key, value]) => {
    if (typeof value === "string" && value.trim()) {
      collected.push([key, value.trim()]);
      return;
    }
    if (value && typeof value === "object") {
      walkObjectStrings(value, depth + 1, visited, collected);
    }
  });
  return collected;
}

function extractSignalByKeys(campaign, keys = []) {
  const target = new Set(keys.map((key) => normalizeLooseKey(key)));
  const strategyPayload =
    campaign?.strategy_payload && typeof campaign.strategy_payload === "object"
      ? campaign.strategy_payload
      : null;
  const entries = walkObjectStrings({
    campaign,
    strategyPayload,
  });

  for (const [key, value] of entries) {
    if (target.has(normalizeLooseKey(key))) return value;
  }
  return "";
}

function normalizeSentence(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const collapsed = raw.replace(/\s+/g, " ").trim();
  return /[.!?]$/.test(collapsed) ? collapsed : `${collapsed}.`;
}

function normalizePlannerList(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(value || "")
    .split(/\s*,\s*/)
    .map((entry) => String(entry || "").trim())
    .filter(Boolean)
    .join(", ");
}

function getPlannerTargetingContext(campaign) {
  const targeting =
    campaign?.targeting ||
    campaign?.strategy_payload?.targeting ||
    campaign?.strategyPayload?.targeting ||
    {};

  const ageMin =
    targeting?.ageMin != null && targeting?.ageMin !== "" ? String(targeting.ageMin).trim() : "";
  const ageMax =
    targeting?.ageMax != null && targeting?.ageMax !== "" ? String(targeting.ageMax).trim() : "";
  const gender = String(targeting?.gender || "").trim().toLowerCase();
  const interests = normalizePlannerList(targeting?.interests);
  const customTargeting = normalizePlannerList(targeting?.customTargeting);
  const geoTargetingNotes = String(
    targeting?.geoTargetingNotes ||
      targeting?.geo_targeting_notes ||
      ""
  ).trim();
  const deviceTargetingNotes = String(targeting?.deviceTargetingNotes || "").trim();
  const audienceNotes = extractSignalByKeys(campaign, [
    "audience notes",
    "audience_note",
    "audienceNotes",
    "audience strategy",
    "target audience",
    "primary audience",
    "audience",
  ]);
  const retargeting =
    String(
      campaign?.retargetingArchitecture?.narrative ||
        campaign?.strategy_payload?.retargetingArchitecture?.narrative ||
        campaign?.strategyPayload?.retargetingArchitecture?.narrative ||
        extractSignalByKeys(campaign, [
          "retargeting",
          "retargeting strategy",
          "retargeting architecture",
        ]) ||
        ""
    ).trim();

  return {
    ageRange:
      ageMin && ageMax
        ? `ages ${ageMin}-${ageMax}`
        : ageMin
        ? `ages ${ageMin}+`
        : ageMax
        ? `up to age ${ageMax}`
        : "",
    gender:
      gender === "female"
        ? "female-skewed targeting"
        : gender === "male"
        ? "male-skewed targeting"
        : "",
    interests,
    customTargeting,
    geoTargetingNotes,
    deviceTargetingNotes,
    audienceNotes,
    retargeting,
  };
}

function joinLabels(labels = []) {
  const clean = Array.from(new Set((labels || []).filter(Boolean)));
  if (clean.length <= 1) return clean[0] || "";
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`;
}

function inferRoleHint(planSignals) {
  const combined = [planSignals.objective, planSignals.rationale, planSignals.channelRole]
    .filter(Boolean)
    .join(" ");
  const hit = KEYWORD_ROLE_HINTS.find((entry) => entry.test.test(combined));
  return hit?.value || "";
}

function getStrategyProfile(platformKey) {
  return PLATFORM_PROFILES[platformKey] || DEFAULT_PROFILE;
}

function buildMixLine(planContext, currentLabel) {
  const remaining = (planContext.approvedPlatformLabels || []).filter(
    (label) => label && label !== currentLabel
  );
  if (!remaining.length) return "This platform is carrying a focused role inside the approved plan.";
  return `It supports a broader mix that also includes ${joinLabels(remaining)}.`;
}

function buildObjectiveLine(planSignals) {
  const preferred = planSignals.objective || planSignals.rationale;
  if (!preferred) return "";
  return normalizeSentence(preferred);
}

export function buildAnalyticsPlanContext({ campaigns, platformKey, platformLabel }) {
  const approved = buildApprovedPlanContext(campaigns);
  const approvedCampaign = approved?.approvedCampaign || null;
  const currentPlatformKey =
    normalizePlatformKey(platformKey) || normalizePlanPlatformKeys(platformKey)[0] || "";
  const currentPlatformLabel = platformLabelFromKey(currentPlatformKey) || platformLabel || "Platform";

  const approvedPlatformKeys = Array.from(
    new Set([
      ...(approved?.approvedPlatformKeys || []),
      ...normalizePlanPlatformKeys(
        approvedCampaign?.target_platforms ||
          approvedCampaign?.targetPlatforms ||
          approvedCampaign?.platforms ||
          approvedCampaign?.strategy_payload?.platforms
      ),
    ])
  );

  const approvedPlatformLabels = approvedPlatformKeys
    .map((key) => platformLabelFromKey(key))
    .filter(Boolean);
  const isPlatformApproved = approvedPlatformKeys.includes(currentPlatformKey);

  const planSignals = {
    objective: extractSignalByKeys(approvedCampaign, [
      "objective",
      "campaign objective",
      "primary objective",
      "goal",
      "marketing objective",
    ]),
    rationale: extractSignalByKeys(approvedCampaign, [
      "strategic rationale",
      "rationale",
      "strategy",
      "summary",
      "campaign summary",
      "why this mix",
    ]),
    audience: extractSignalByKeys(approvedCampaign, [
      "audience",
      "target audience",
      "primary audience",
      "audience strategy",
    ]),
    channelRole: extractSignalByKeys(approvedCampaign, [
      "channel role",
      "platform role",
      "role",
    ]),
  };
  const plannerTargeting = getPlannerTargetingContext(approvedCampaign);

  return {
    approvedCampaign,
    currentPlatformKey,
    currentPlatformLabel,
    approvedPlatformKeys,
    approvedPlatformLabels,
    hasApprovedPlan: Boolean(approvedCampaign),
    isPlatformApproved,
    profile: getStrategyProfile(currentPlatformKey),
    planSignals,
    plannerTargeting,
    roleHint: inferRoleHint(planSignals),
  };
}

export function buildPlanningAudienceContext(planContext) {
  if (!planContext?.hasApprovedPlan) return null;

  const targeting = planContext.plannerTargeting || {};
  const summaryParts = [
    targeting.ageRange,
    targeting.gender,
    targeting.interests ? `interests in ${targeting.interests}` : "",
    targeting.customTargeting ? `custom targeting in ${targeting.customTargeting}` : "",
    targeting.geoTargetingNotes,
    targeting.retargeting ? `with ${targeting.retargeting.replace(/\.$/, "")}` : "",
  ].filter(Boolean);

  const bullets = [
    "Planning audience context is based on approved SherlocMediaPlanner targeting, not live platform audience composition.",
    targeting.audienceNotes ? normalizeSentence(targeting.audienceNotes) : "",
    targeting.deviceTargetingNotes ? normalizeSentence(targeting.deviceTargetingNotes) : "",
    planContext.profile?.audienceRole
      ? normalizeSentence(`Platform targeting assumption: ${planContext.profile.audienceRole}`)
      : "",
  ].filter(Boolean);

  if (!summaryParts.length && !bullets.length) return null;

  return {
    available: false,
    summary: summaryParts.length
      ? `Live audience breakdown is not available yet. Planned target audience: ${joinLabels(
          summaryParts
        )}. Use this as planning context until platform-level audience composition is available.`
      : "Live audience breakdown is not available yet. Use the approved targeting plan as planning context until platform-level audience composition is available.",
    bullets,
    status: "Planning Audience Context",
  };
}

function buildApprovedSectionCopy(section, planContext, { reportAvailable = true } = {}) {
  const label = planContext.currentPlatformLabel;
  const { profile, planSignals, roleHint } = planContext;
  const mixLine = buildMixLine(planContext, label);
  const objectiveLine = buildObjectiveLine(planSignals);
  const audienceSignal = planSignals.audience
    ? normalizeSentence(planSignals.audience)
    : "";
  const roleLead = roleHint ? ` The approved strategy leans on ${label} for ${roleHint}.` : "";

  if (section === "performance") {
    return {
      summary: `${label} is positioned in the approved media plan as ${profile.performanceRole}.${roleLead} ${mixLine} Live delivery metrics will sharpen this read as the campaign accumulates signal.`.replace(
        /\s+/g,
        " "
      ),
      bullets: [objectiveLine, reportAvailable ? "" : `No ${label} report is configured for this advertiser yet.`].filter(Boolean),
    };
  }

  if (section === "audience") {
    return {
      summary: `${label} is expected to engage ${profile.audienceRole}. ${mixLine} Audience intelligence will sharpen as engagement and conversion data accumulate.`.replace(
        /\s+/g,
        " "
      ),
      bullets: [audienceSignal, objectiveLine].filter(Boolean),
    };
  }

  if (section === "platform") {
    return {
      summary: `${label} supports the approved mix by ${profile.platformRole}. ${mixLine}`.replace(
        /\s+/g,
        " "
      ),
      bullets: [objectiveLine, audienceSignal].filter(Boolean),
    };
  }

  if (section === "regional") {
    return {
      summary: `${label} is expected to contribute through ${profile.regionalRole}. Geographic intelligence will sharpen as delivery resolves market-level detail.`.replace(
        /\s+/g,
        " "
      ),
      bullets: [objectiveLine, audienceSignal].filter(Boolean),
    };
  }

  return null;
}

function buildUnapprovedSectionCopy(section, planContext, { reportAvailable = true } = {}) {
  const label = planContext.currentPlatformLabel;
  const approvedMix = joinLabels(planContext.approvedPlatformLabels);
  const approvedMixLine = approvedMix
    ? `The approved mix currently prioritizes ${approvedMix}.`
    : "No approved platform mix has been resolved yet.";

  const summaryBySection = {
    performance: `${label} is not explicitly called out in the advertiser's approved media plan. Live performance intelligence on this page will become more specific once delivery data or a planning role is confirmed.`,
    audience: `${label} is not explicitly defined as an approved audience layer in the current media plan, so audience guidance is staying neutral until stronger delivery or planning context appears.`,
    platform: `${label} is not explicitly part of the approved media mix for this advertiser. Platform intelligence will stay conservative until the plan or live delivery confirms a role here.`,
    regional: `${label} is not explicitly defined in the approved media mix, so regional guidance will stay conservative until geo-level delivery confirms a role on this page.`,
  };

  return {
    summary: summaryBySection[section] || summaryBySection.platform,
    bullets: [approvedMixLine, reportAvailable ? "" : `No ${label} report is configured for this advertiser yet.`].filter(Boolean),
  };
}

export function buildPlanAwareFallback(section, planContext, options = {}) {
  if (!planContext?.hasApprovedPlan) return null;
  if (planContext.isPlatformApproved) {
    return buildApprovedSectionCopy(section, planContext, options);
  }
  return buildUnapprovedSectionCopy(section, planContext, options);
}
